import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  tmsApi,
  type DispatchMessage,
  type DispatchProposals,
  type DriverInbox,
  type OrderDispatches,
  type SendDispatchRequest,
} from '../client';
import { orderCheckpointKeys } from './useOrderCheckpoints';

export const dispatchKeys = {
  all: ['tms', 'dispatch'] as const,
  byOrder: (orderId: string) => ['tms', 'dispatch', 'order', orderId] as const,
  proposals: (orderId: string) => ['tms', 'dispatch', 'proposals', orderId] as const,
  messages: (chainId: string) => ['tms', 'dispatch', 'messages', chainId] as const,
  inbox: (organisationId: string, scope: string) => ['tms', 'dispatch', 'inbox', organisationId, scope] as const,
};

/**
 * Driver messages across every order — the inbox behind the top-right icon.
 * Polls every 30 seconds, while the tab is in front, so a query turns up
 * without anyone opening each order.
 */
export function useDriverInbox(organisationId: string | null, scope: 'mine' | 'all', enabled = true) {
  return useQuery<DriverInbox, Error>({
    queryKey: organisationId ? dispatchKeys.inbox(organisationId, scope) : dispatchKeys.all,
    queryFn: () => tmsApi.getDriverInbox(organisationId!, scope),
    enabled: enabled && !!organisationId,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

/** The order's jobs on drivers' phones, live and past. Polls while open, for accepts and replies. */
export function useOrderDispatches(organisationId: string | null, orderId: string | null) {
  return useQuery<OrderDispatches, Error>({
    queryKey: orderId ? dispatchKeys.byOrder(orderId) : dispatchKeys.all,
    queryFn: () => {
      if (!organisationId || !orderId) throw new Error('No order');
      return tmsApi.getOrderDispatches(organisationId, orderId);
    },
    enabled: !!organisationId && !!orderId,
    staleTime: 15 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/** Who could be sent what — only while the send screen is open. */
export function useDispatchProposals(organisationId: string | null, orderId: string | null, enabled: boolean) {
  return useQuery<DispatchProposals, Error>({
    queryKey: orderId ? dispatchKeys.proposals(orderId) : dispatchKeys.all,
    queryFn: () => {
      if (!organisationId || !orderId) throw new Error('No order');
      return tmsApi.prepareDispatch(organisationId, orderId);
    },
    enabled: enabled && !!organisationId && !!orderId,
    staleTime: 0,
  });
}

export function useDispatchActions(organisationId: string | null, orderId: string | null) {
  const qc = useQueryClient();
  const settle = (next: OrderDispatches) => {
    if (!orderId) return;
    qc.setQueryData(dispatchKeys.byOrder(orderId), next);
    qc.invalidateQueries({ queryKey: dispatchKeys.proposals(orderId) });
    qc.invalidateQueries({ queryKey: orderCheckpointKeys.byOrder(orderId) });
  };
  const send = useMutation<OrderDispatches, Error, Omit<SendDispatchRequest, 'organisationId'>>({
    mutationFn: (body) => {
      if (!organisationId || !orderId) throw new Error('No order');
      return tmsApi.sendDispatch(orderId, { ...body, organisationId });
    },
    onSuccess: settle,
  });
  const withdraw = useMutation<OrderDispatches, Error, string>({
    mutationFn: (dispatchId) => {
      if (!organisationId) throw new Error('No organisation');
      return tmsApi.withdrawDispatch(organisationId, dispatchId);
    },
    onSuccess: settle,
  });
  return { send, withdraw, busy: send.isPending || withdraw.isPending };
}

/**
 * The thread on one job (it survives updates). Reading it marks the driver's
 * messages read; polls while open so a reply turns up.
 */
export function useDispatchMessages(organisationId: string | null, chainId: string | null, orderId: string | null) {
  const qc = useQueryClient();
  const query = useQuery<DispatchMessage[], Error>({
    queryKey: chainId ? dispatchKeys.messages(chainId) : dispatchKeys.all,
    queryFn: async () => {
      if (!organisationId || !chainId) throw new Error('No thread');
      const messages = await tmsApi.getDispatchMessages(organisationId, chainId);
      // Their unread count just went to zero — on the order and in the inbox.
      if (orderId) qc.invalidateQueries({ queryKey: dispatchKeys.byOrder(orderId) });
      qc.invalidateQueries({ queryKey: ['tms', 'dispatch', 'inbox'] });
      return messages;
    },
    enabled: !!organisationId && !!chainId,
    refetchInterval: 20 * 1000,
  });
  const post = useMutation<DispatchMessage[], Error, string>({
    mutationFn: (text) => {
      if (!organisationId || !chainId) throw new Error('No thread');
      return tmsApi.postDispatchMessage(organisationId, chainId, text);
    },
    onSuccess: (next) => {
      if (chainId) qc.setQueryData(dispatchKeys.messages(chainId), next);
      if (orderId) qc.invalidateQueries({ queryKey: dispatchKeys.byOrder(orderId) });
    },
  });
  return { ...query, post };
}
